import { ApolloLink, Observable } from '@apollo/client';
import { loginPath } from '@components/modules/auth/urls';

import { forceRefreshToken } from './fetchQuery/accessTokenApiChecks';
import { isPublicPath } from './publicPaths';
import { useCookiesManager } from './useCookiesManager';

let refreshPromise: Promise<string | null> | null = null;

// this ensures only one refresh happens at a time
const getRefreshPromise = (): Promise<string | null> => {
  if (!refreshPromise) {
    // The server already rejected the current token, so always mint a fresh
    // one. Checking local expiry here would re-send the rejected token when
    // the client clock disagrees with the server, guaranteeing a second
    // failure and, from there, a login redirect loop.
    refreshPromise = forceRefreshToken().finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
};

const isTokenExpiredError = (graphQLErrors?: readonly any[]): boolean => {
  if (!graphQLErrors) return false;

  return graphQLErrors.some((error) => {
    return (
      ['JWT_TOKEN_EXPIRED', 'JWT_TOKEN_INVALID', 'UNAUTHENTICATED'].includes(
        error.extensions?.code
      ) || error.message?.includes('expired')
    );
  });
};

const completeLogout = () => {
  if (!isPublicPath(window.location.pathname)) {
    useCookiesManager.clearAll();
    window.location.href = loginPath;
  }
};

//Check if operation is a refresh token mutation to avoid retrying it
const isRefreshTokenOperation = (operation: ApolloLink.Operation): boolean => {
  return operation.operationName === 'RefreshToken';
};

export const createAuthLink = () => {
  return new ApolloLink((operation: ApolloLink.Operation, forward) => {
    const accessToken = useCookiesManager.getAccessToken();

    if (accessToken) {
      operation.setContext(({ headers = {} }) => ({
        headers: {
          ...headers,
          Authorization: `Bearer ${accessToken}`,
        },
      }));
    } else {
      completeLogout();
    }

    return new Observable((observer) => {
      let subscription: any;
      // Track if this specific operation has been retried
      const context = operation.getContext();
      const hasRetried = context.hasRetriedAuth === true;

      const execute = () => {
        subscription = forward(operation).subscribe({
          next: (result: ApolloLink.Result) => {
            // Never retry the refresh token mutation itself
            if (isRefreshTokenOperation(operation)) {
              observer.next(result);

              return;
            }

            if (isTokenExpiredError(result.errors) && !hasRetried) {
              // Don't pass the error result to observer yet
              // Unsubscribe from the original failed subscription
              if (subscription) {
                subscription.unsubscribe();
              }

              getRefreshPromise()
                .then((newAccessToken) => {
                  if (newAccessToken) {
                    operation.setContext(({ headers = {} }) => ({
                      headers: {
                        ...headers,
                        Authorization: `Bearer ${newAccessToken}`,
                      },
                      hasRetriedAuth: true,
                    }));
                    // Retry the operation - let the retry subscription handle all callbacks
                    subscription = forward(operation).subscribe({
                      next: observer.next.bind(observer),
                      error: observer.error.bind(observer),
                      complete: observer.complete.bind(observer),
                    });
                  } else {
                    useCookiesManager.clearAll();
                    observer.error(new Error('Authentication failed'));
                  }
                })
                .catch((error) => {
                  useCookiesManager.clearAll();
                  observer.error(error);
                });
            } else {
              // No auth error or already retried - pass through
              observer.next(result);
            }
          },
          error: observer.error.bind(observer),
          complete: observer.complete.bind(observer),
        });
      };

      execute();

      return () => {
        if (subscription) {
          subscription.unsubscribe();
        }
      };
    });
  });
};
